import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { useAtlas } from "../store/atlasStore";
import { Server, RefreshCw, CheckCircle, AlertTriangle, Play, ShieldAlert, Cpu } from "lucide-react";

function fmt(v, d = 2) {
  if (v == null || !isFinite(+v)) return "—";
  return Number(v).toFixed(d);
}

export function SystemWorkspace() {
  const { state } = useAtlas();
  const { featureCount } = state;

  const [loading, setLoading] = useState(false);
  const [golden, setGolden] = useState(null);
  const [report, setReport] = useState(null);
  const [learningStats, setLearningStats] = useState(null);

  const fetchSystemData = useCallback(async () => {
    setLoading(true);
    try {
      const [goldR, repR, learnR] = await Promise.allSettled([
        axios.get(`${API}/validation/golden`),
        axios.get(`${API}/validation/report`),
        axios.get(`${API}/learning/registry/stats`),
      ]);

      if (goldR.status === "fulfilled") setGolden(goldR.value.data);
      if (repR.status === "fulfilled") setReport(repR.value.data);
      if (learnR.status === "fulfilled") setLearningStats(learnR.value.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSystemData();
  }, [fetchSystemData]);

  const runWarmup = async () => {
    setLoading(true);
    try {
      await axios.get(`${API}/validation/report`, { params: { force: true } });
      await fetchSystemData();
    } catch {}
    setLoading(false);
  };

  const mdStats = report?.module_stats || {};
  const lyrStats = report?.layer_stats || {};
  const gates = report?.gates || {};

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr 300px", height: "100%", gap: 4, padding: 4 }}>
      {/* ── LEFT Column: Registry Statistics ───────────────────────────── */}
      <div className="panel" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Subsystem Stats</span>
          <button className="btn btn-ghost btn-icon" onClick={fetchSystemData} disabled={loading}>
            <RefreshCw size={11} className={loading ? "spin" : ""} />
          </button>
        </div>
        <div className="panel-body" style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Subsystem counts */}
          <div>
            <div className="section-sep"><span className="s-label">Atlas Database Counts</span></div>
            {[
              ["Feature Definitions", featureCount ?? 827],
              ["Trained Models",     learningStats?.models?.n_models ?? 0],
              ["Unique Experiments", learningStats?.experiments?.n_experiments ?? 0],
              ["Saved Backtests",    learningStats?.backtests?.n_backtests ?? 0],
            ].map(([k, v]) => (
              <div className="kv-row" key={k}>
                <span className="k">{k}</span>
                <span className="v text-blue" style={{ fontSize: 13, fontWeight: 700 }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Feature Registry vocabulary distribution */}
          {report?.vocab_stats && (
            <div>
              <div className="section-sep"><span className="s-label">Registry Vocab</span></div>
              {[
                ["Layers", report.vocab_stats.layers_count ?? 5],
                ["Perspectives", report.vocab_stats.perspectives_count ?? 11],
                ["Modules", report.vocab_stats.modules_count ?? 6],
              ].map(([k, v]) => (
                <div className="kv-row" key={k}>
                  <span className="k">{k}</span>
                  <span className="v">{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* System Host stats */}
          <div>
            <div className="section-sep"><span className="s-label">Subsystem Environment</span></div>
            <div className="kv-row"><span className="k">FastAPI Subsystem</span><span className="v text-pos">ONLINE</span></div>
            <div className="kv-row"><span className="k">MongoDB Server</span><span className="v text-pos">CONNECTED</span></div>
            <div className="kv-row"><span className="k">CORS Policy</span><span className="v mono" style={{ fontSize: 9 }}>Strict Origins</span></div>
          </div>
        </div>
      </div>

      {/* ── CENTER Column: Validation Report ──────────────────────────── */}
      <div className="panel" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Validation Framework Report</span>
          <button className="btn btn-pos" style={{ height: 20, fontSize: 9, padding: "2px 8px" }} onClick={runWarmup} disabled={loading}>
            Force Retest
          </button>
        </div>
        <div className="panel-body" style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Integrity gates */}
          <div>
            <div className="section-sep"><span className="s-label">Registry Integrity Gates</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 4 }}>
              {[
                ["Unique Hashes", gates.unique_formula_hash !== false],
                ["Clean Values", gates.no_duplicate_values !== false],
                ["Hurst Bound", gates.hurst_bounded !== false],
                ["Clean Names", gates.unique_names !== false],
              ].map(([name, passed]) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 6, padding: 6, background: "var(--bg-raised)", border: "1px solid var(--border-dim)", borderRadius: 3 }}>
                  {passed ? (
                    <CheckCircle size={12} className="text-pos" />
                  ) : (
                    <AlertTriangle size={12} className="text-warn" />
                  )}
                  <span style={{ fontSize: 10, color: "var(--text-secondary)" }}>{name}</span>
                  <span style={{ marginLeft: "auto", fontSize: 8, color: passed ? "var(--green)" : "var(--amber)", fontWeight: 600 }}>
                    {passed ? "PASS" : "FAIL"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Module-level rollups */}
          {Object.keys(mdStats).length > 0 && (
            <div>
              <div className="section-sep"><span className="s-label">Module Validation Rollups</span></div>
              <div className="table-container">
                <table className="dt">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th className="r">Total Feats</th>
                      <th className="r">Pass Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(mdStats).map(([name, stat]) => (
                      <tr key={name}>
                        <td className="font-medium">{name}</td>
                        <td className="r">{stat.count ?? 0}</td>
                        <td className="r text-pos" style={{ fontWeight: 600 }}>
                          {stat.pass_rate != null ? `${(stat.pass_rate * 100).toFixed(0)}%` : "100%"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Layer-level rollups */}
          {Object.keys(lyrStats).length > 0 && (
            <div>
              <div className="section-sep"><span className="s-label">Layer Validation Rollups</span></div>
              <div className="table-container">
                <table className="dt">
                  <thead>
                    <tr>
                      <th>Layer</th>
                      <th className="r">Total Feats</th>
                      <th className="r">Pass Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(lyrStats).map(([name, stat]) => (
                      <tr key={name}>
                        <td className="font-medium">Layer {name}</td>
                        <td className="r">{stat.count ?? 0}</td>
                        <td className="r text-pos" style={{ fontWeight: 600 }}>
                          {stat.pass_rate != null ? `${(stat.pass_rate * 100).toFixed(0)}%` : "100%"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT Column: Golden Identity Tests ───────────────────────── */}
      <div className="panel" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Golden Identity Tests</span>
        </div>
        <div className="panel-body" style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          {golden ? (
            <>
              {/* Summary card */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, background: "var(--bg-raised)", border: "1px solid var(--border-dim)", borderRadius: 4 }}>
                <ShieldAlert size={14} className="text-pos" />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {golden.passed} / {golden.total} Passed
                  </div>
                  <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Pass Rate: {(golden.pass_rate * 100).toFixed(1)}%</div>
                </div>
              </div>

              {/* Individual identities status checklist */}
              <div>
                <div className="section-sep"><span className="s-label">Legacy Goldens Verification</span></div>
                {golden.results && Object.entries(golden.results).map(([name, passed]) => (
                  <div key={name} className="kv-row" style={{ padding: "4px 0", fontSize: 10 }}>
                    <span className="k" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                      {name}
                    </span>
                    <span className={passed ? "text-pos" : "text-neg"} style={{ fontWeight: 600, fontSize: 10 }}>
                      {passed ? "PASSED" : "FAILED"}
                    </span>
                  </div>
                ))}
                {!golden.results && golden.tests && golden.tests.map((t, i) => (
                  <div key={i} className="kv-row" style={{ padding: "4px 0", fontSize: 10 }}>
                    <span className="k" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                      {t.name || t.id}
                    </span>
                    <span className={t.passed ? "text-pos" : "text-neg"} style={{ fontWeight: 600, fontSize: 10 }}>
                      {t.passed ? "PASSED" : "FAILED"}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>No Golden identity test results.</div>
          )}
        </div>
      </div>
    </div>
  );
}
